import { ChevronDown, Check } from "lucide-react"
import { type ComponentPropsWithoutRef } from "react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

type StatusSelectOption = {
  id: string
  label: string
  color: string
}

type StatusSelectTriggerProps = ComponentPropsWithoutRef<"button"> &
  Partial<Record<`data-${string}`, string | number | undefined>>

type StatusSelectProps<TOption extends StatusSelectOption> = {
  value?: TOption["id"] | null
  onChange: (value: TOption["id"]) => void
  options: readonly TOption[]
  placeholder?: string
  className?: string
  triggerProps?: StatusSelectTriggerProps
}

const FALLBACK_BG = "#E2E8F0"
const FALLBACK_TEXT = "#0F172A"

const sanitizeHex = (hex: string) => hex.trim().replace("#", "")

const expandShorthandHex = (hex: string) =>
  hex.length === 3
    ? hex
      .split("")
      .map((char) => char + char)
      .join("")
    : hex

const parseHexColor = (hex: string) => {
  const sanitized = expandShorthandHex(sanitizeHex(hex))

  if (!/^([0-9A-F]{6})$/i.test(sanitized)) {
    return null
  }

  const value = parseInt(sanitized, 16)

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

const toHex = (value: number) =>
  Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, "0")

const lightenHexColor = (hex: string, amount = 0.12) => {
  const rgb = parseHexColor(hex)

  if (!rgb) return hex

  const lighten = (channel: number) => channel + (255 - channel) * amount

  return `#${toHex(lighten(rgb.r))}${toHex(lighten(rgb.g))}${toHex(lighten(rgb.b))}`
}

const getReadableTextColor = (hex: string) => {
  const rgb = parseHexColor(hex)

  if (!rgb) return "#FFFFFF"

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255

  return luminance > 0.6 ? "#0F172A" : "#FFFFFF"
}

export const StatusSelect = <TOption extends StatusSelectOption>({
  value,
  onChange,
  options,
  placeholder = "Selecteer status",
  className,
  triggerProps,
}: StatusSelectProps<TOption>) => {
  const activeOption = options.find((option) => option.id === value) ?? null

  const baseColor = activeOption?.color ?? FALLBACK_BG
  const textColor = activeOption ? getReadableTextColor(baseColor) : FALLBACK_TEXT
  const arrowBg = lightenHexColor(baseColor, 0.18)
  const arrowColor = activeOption ? textColor : FALLBACK_TEXT
  const {
    className: triggerClassName,
    style: triggerStyle,
    ["aria-label"]: triggerAriaLabel,
    ...restTriggerProps
  } = triggerProps ?? {}

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={triggerAriaLabel ?? "Status wijzigen"}
          data-slot="status-select-trigger"
          className={cn(
            "flex items-center gap-3 rounded-full pl-4 pr-2 py-2 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            className,
            triggerClassName
          )}
          style={{
            backgroundColor: baseColor,
            color: textColor,
            ...triggerStyle,
          }}
          {...restTriggerProps}
        >
          <span className="truncate">{activeOption?.label ?? placeholder}</span>
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full border border-white/30 transition-colors"
            style={{
              backgroundColor: arrowBg,
              color: arrowColor,
            }}
            aria-hidden
          >
            <ChevronDown className="size-4" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[220px] rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
      >
        {options.map((option) => {
          const isActive = option.id === value
          const optionTextColor = getReadableTextColor(option.color)

          return (
            <DropdownMenuItem
              key={option.id}
              className="flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium"
              onSelect={(event) => {
                event.stopPropagation()
                onChange(option.id)
              }}
            >
              <span className="flex items-center gap-2">
                <span
                  className="size-2.5 rounded-full"
                  style={{ backgroundColor: option.color }}
                  aria-hidden
                />
                <span>{option.label}</span>
              </span>
              {isActive ? (
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full flex-shrink-0"
                  style={{ backgroundColor: option.color, color: optionTextColor }}
                >
                  <Check className="size-4" color="white" />
                </span>
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

StatusSelect.displayName = "StatusSelect"

export type { StatusSelectOption }
